import React, { useState, useEffect } from 'react';
import { getOverlayPrefs, setOverlayPref, subscribeOverlayPrefs } from '../common/overlayPrefs';
import { getTheme, setTheme } from '../util/theme';
import { hasWebCodecs } from '../util/detectWebSocketSupport';

/**
 * DisplaySetup (unified-viewer-setup-design.md §4.5, D6) — the viewer-CLIENT-side prefs module.
 * The one setup module identical across ALL product modes: it configures the viewer, not the
 * device/NVR, so there is no backend write. Everything persists to localStorage.
 *
 * v1: appearance (theme) + overlay toggles (info/objects/lpr, backed by the shared overlayPrefs
 * so Setup and the in-view control drive the SAME state and every live player updates instantly).
 *
 * Styling is className-driven (setupStyle.css, scoped under `.setup-container`) per the house
 * design system; fluid clamp() sizing, mobile-first.
 */
const OVERLAY_ROWS = [
  { key: 'info', label: 'Name & Timestamp', desc: 'Camera name + timestamp text drawn on the video.' },
  { key: 'objects', label: 'Object Detection', desc: 'AI object-detection boxes + labels (where available).' },
  { key: 'lpr', label: 'License Plates', desc: 'License-plate boxes + plate text (where available).' },
];

const THEMES = [
  { id: 'dark', label: 'Dark' },
  { id: 'light', label: 'Light' },
  { id: 'browser', label: 'Browser Default' },
];

// "Browser Default" = follow the OS/browser appearance. theme.js stores a concrete 'light'|'dark', so
// (matching the old overlay) we keep a separate `dview-theme-preference='browser'` flag for the selected
// state and resolve the concrete theme from prefers-color-scheme when it's picked.
const systemTheme = () => (typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light';
const readThemePref = () => {
  try { if (localStorage.getItem('dview-theme-preference') === 'browser') return 'browser'; } catch (e) { /* ignore */ }
  return getTheme();
};

const DisplaySetup = () => {
  const [prefs, setPrefs] = useState(getOverlayPrefs());
  const [themePref, setThemePref] = useState(readThemePref());

  // Live-sync with the in-view overlay control (both write the shared overlayPrefs).
  useEffect(() => subscribeOverlayPrefs(setPrefs), []);

  const toggle = (key) => setOverlayPref(key, !prefs[key]);
  // v1 coarse gate (see CameraOverlaysContent): hide the ws/WebCodecs-only overlays (Object
  // Detection, License Plates) when the browser can't ws-stream; Name & Timestamp always shows.
  const overlayRows = OVERLAY_ROWS.filter((r) => r.key === 'info' || hasWebCodecs());
  const changeTheme = (t) => {
    if (t === 'browser') {
      setTheme(systemTheme());                                        // resolve + apply the OS appearance
      try { localStorage.setItem('dview-theme-preference', 'browser'); } catch (e) { /* ignore */ }
    } else {
      setTheme(t);
      try { localStorage.removeItem('dview-theme-preference'); } catch (e) { /* ignore */ }
    }
    setThemePref(readThemePref());
  };

  return (
    <div className="setup-display">
      <div className="setup-screen__title">Display</div>
      <div className="setup-display__sub">These preferences are stored in this browser and apply to your viewer only.</div>

      <div className="setup-section">Appearance</div>
      <div className="setup-theme-row">
        {THEMES.map((t) => (
          <button
            key={t.id}
            className={'setup-theme-btn' + (themePref === t.id ? ' setup-theme-btn--active' : '')}
            onClick={() => changeTheme(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="setup-section">Overlays</div>
      {/* Neutral wrapper: stacks (block) in portrait — no visual change from the base — and goes
          side-by-side in mobile landscape so the cards use the width instead of a narrow column. */}
      <div className="setup-display__overlays">
        {overlayRows.map((row) => (
          <label key={row.key} className="setup-overlay-row">
            <input
              type="checkbox"
              checked={!!prefs[row.key]}
              onChange={() => toggle(row.key)}
              className="setup-overlay-row__checkbox"
            />
            <span className="setup-overlay-row__text">
              <span className="setup-overlay-row__label">{row.label}</span>
              <span className="setup-overlay-row__desc">{row.desc}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default DisplaySetup;
